import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { axiosInstance } from '../../../engine/config/axios'
import { toast } from '../../../_helpers/toasts'
import DocumentTextFormGroup from './DocumentTextFormGroup'
import DocumentFileFormGroup from './DocumentFileFormGroup'

interface ProfileDocuments {
  passport?: { series_number?: string; files?: unknown[] }
  registration?: { address?: string; files?: unknown[] }
  international_passport?: { latin_name?: string; files?: unknown[] }
  state_register_extract?: { files?: unknown[] }
}

interface ProfileData {
  documents?: ProfileDocuments
}

interface Params {
  userId: number
}

type SubmitState = 'form' | 'loading' | 'success'

export default function UserDocuments({ userId }: Params) {
  const { t } = useTranslation()
  const [passportSeriesNumber, setPassportSeriesNumber] = useState('')
  const [registrationAddress, setRegistrationAddress] = useState('')
  const [internationalPassportName, setInternationalPassportName] = useState('')
  const [submitState, setSubmitState] = useState<SubmitState>('form')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  const [profileData, setProfileData] = useState<ProfileData | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)

  const passportFilesRef = useRef<File[]>([])
  const registrationFileRef = useRef<File[]>([])
  const internationalPassportFileRef = useRef<File[]>([])
  const stateRegisterFileRef = useRef<File[]>([])

  // Reset keys to force FileUploader remount (clears files)
  const [passportKey, setPassportKey] = useState(0)
  const [registrationKey, setRegistrationKey] = useState(0)
  const [internationalKey, setInternationalKey] = useState(0)
  const [stateRegisterKey, setStateRegisterKey] = useState(0)

  useEffect(() => {
    axiosInstance.get(`/employee/${userId}/documents/profile`)
      .then(res => setProfileData(res.data?.profile ?? null))
      .catch(() => setProfileData(null))
      .finally(() => setProfileLoading(false))
  }, [userId])

  useEffect(() => {
    if (profileLoading || profileData === null) return
    const docs = profileData?.documents
    const allFilled = !!(
      docs?.passport?.series_number
      && docs?.passport?.files?.length
      && docs?.registration?.address
      && docs?.registration?.files?.length
      && docs?.international_passport?.latin_name
      && docs?.international_passport?.files?.length
      && docs?.state_register_extract?.files?.length
    )
    if (allFilled) {
      document.querySelector<HTMLElement>('i.fa.fa-exclamation-triangle.text-warning[title="Documents not filled"]')?.remove()
    }
  }, [profileData, profileLoading])

  // Show indicators once loading is done — null profile means nothing is filled yet
  const driveScanned = !profileLoading

  const isPassportTextEmpty  = driveScanned && !profileData?.documents?.passport?.series_number
  const isPassportFilesEmpty = driveScanned && !profileData?.documents?.passport?.files?.length

  const isRegistrationTextEmpty  = driveScanned && !profileData?.documents?.registration?.address
  const isRegistrationFilesEmpty = driveScanned && !profileData?.documents?.registration?.files?.length

  const isInternationalTextEmpty  = driveScanned && !profileData?.documents?.international_passport?.latin_name
  const isInternationalFilesEmpty = driveScanned && !profileData?.documents?.international_passport?.files?.length

  const isStateRegisterFilesEmpty = driveScanned && !profileData?.documents?.state_register_extract?.files?.length

  const clearFieldError = (field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const hasAnyData = () => passportSeriesNumber.trim() !== ''
      || registrationAddress.trim() !== ''
      || internationalPassportName.trim() !== ''
      || passportFilesRef.current.length > 0
      || registrationFileRef.current.length > 0
      || internationalPassportFileRef.current.length > 0
      || stateRegisterFileRef.current.length > 0

  const resetForm = () => {
    setPassportSeriesNumber('')
    setRegistrationAddress('')
    setInternationalPassportName('')
    passportFilesRef.current = []
    registrationFileRef.current = []
    internationalPassportFileRef.current = []
    stateRegisterFileRef.current = []
    setPassportKey(prev => prev + 1)
    setRegistrationKey(prev => prev + 1)
    setInternationalKey(prev => prev + 1)
    setStateRegisterKey(prev => prev + 1)
  }

  const handleSubmit = async () => {
    if (!hasAnyData()) {
      toast.show(t('documents.validation_warning'), 'Validation', 'warning', false)
      return
    }

    setFieldErrors({})
    setSubmitState('loading')
    window.scrollTo({ top: 0, behavior: 'smooth' })

    try {
      const formData = new FormData()

      if (passportSeriesNumber.trim()) {
        formData.append('passport_series_number', passportSeriesNumber.trim())
      }
      if (registrationAddress.trim()) {
        formData.append('registration_address', registrationAddress.trim())
      }
      if (internationalPassportName.trim()) {
        formData.append('international_passport_name', internationalPassportName.trim())
      }

      passportFilesRef.current.forEach(file => {
        formData.append('passport_files[]', file)
      })
      registrationFileRef.current.forEach(file => {
        formData.append('registration_file', file)
      })
      internationalPassportFileRef.current.forEach(file => {
        formData.append('international_passport_file', file)
      })
      stateRegisterFileRef.current.forEach(file => {
        formData.append('state_register_extract_file', file)
      })

      await axiosInstance.post(`/employee/${userId}/documents/save`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      resetForm()
      setSubmitState('success')

      // Refresh profile data after successful save so indicators update
      axiosInstance.get(`/employee/${userId}/documents/profile`)
        .then(res => setProfileData(res.data?.profile ?? null))
        .catch(_err => setProfileData(null))
    } catch (error) {
      const err = error as { response?: { data?: { error?: string; errors?: unknown } } }
      const responseErrors = err?.response?.data?.errors
      if (responseErrors && typeof responseErrors === 'object' && !Array.isArray(responseErrors)) {
        setFieldErrors(responseErrors as Record<string, string[]>)
      } else {
        const message = err?.response?.data?.error
          || (Array.isArray(responseErrors) ? (responseErrors as string[]).join(', ') : '')
          || t('documents.save_error')
        toast.show(message, 'Error', 'error', false)
      }
      setSubmitState('form')
    }
  }

  if (submitState === 'success') {
    return (
      <div style={{ padding: '15px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#28a745', fontSize: '2rem', lineHeight: 1 }}>&#10003;</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 500, color: '#28a745' }}>{t('documents.save_success_title')}</span>
        </div>
        <button
          type="button"
          className="btn waves-effect waves-light btn-outline-secondary"
          onClick={() => setSubmitState('form')}
        >
          {t('documents.back_to_form')}
        </button>
      </div>
    )
  }

  const notFilledLabel = t('documents.not_filled')
  const onValidationError = (msg: string) => toast.show(msg, 'Validation', 'warning', false)

  return (
    <div style={{ padding: '15px 0' }}>
      {submitState === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <div className="spinner-border text-info" role="status">
            <span className="sr-only">{t('documents.saving')}</span>
          </div>
          <span style={{ color: '#17a2b8', fontWeight: 500, marginTop: '10px' }}>{t('documents.saving')}</span>
        </div>
      )}
      <div style={{ display: submitState === 'loading' ? 'none' : undefined }}>
        <div>
          {/* Passport */}
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">{t('documents.passport_title')}</h5>
              <DocumentTextFormGroup
                id="passport_series_number"
                label={t('documents.passport_series_number')}
                placeholder={t('documents.passport_series_placeholder')}
                value={passportSeriesNumber}
                onChange={setPassportSeriesNumber}
                isEmptyInDrive={isPassportTextEmpty}
                notFilledLabel={notFilledLabel}
                error={fieldErrors.passport_series_number?.[0]}
                onClearError={() => clearFieldError('passport_series_number')}
              />
              <DocumentFileFormGroup
                label={t('documents.passport_files')}
                isEmptyInDrive={isPassportFilesEmpty}
                notFilledLabel={notFilledLabel}
                uploaderKey={passportKey}
                multiple
                accept=".jpg,.jpeg,.png,.pdf"
                maxSizeInMB={20}
                formats={['jpg', 'jpeg', 'png', 'pdf']}
                onFilesAdded={(files) => { passportFilesRef.current = [...passportFilesRef.current, ...files] }}
                onDelete={() => { passportFilesRef.current = passportFilesRef.current.slice(0, -1) }}
                onValidationError={onValidationError}
                error={fieldErrors.passport_files?.[0]}
                onClearError={() => clearFieldError('passport_files')}
                hint={t('documents.passport_merge_hint')}
              />
            </div>
          </div>

          {/* Registration */}
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">{t('documents.registration_title')}</h5>
              <DocumentTextFormGroup
                id="registration_address"
                label={t('documents.registration_address')}
                placeholder={t('documents.registration_address_placeholder')}
                value={registrationAddress}
                onChange={setRegistrationAddress}
                isEmptyInDrive={isRegistrationTextEmpty}
                notFilledLabel={notFilledLabel}
                error={fieldErrors.registration_address?.[0]}
                onClearError={() => clearFieldError('registration_address')}
                multiline
                rows={2}
              />
              <DocumentFileFormGroup
                label={t('documents.registration_file')}
                isEmptyInDrive={isRegistrationFilesEmpty}
                notFilledLabel={notFilledLabel}
                uploaderKey={registrationKey}
                multiple={false}
                accept=".jpg,.jpeg,.png,.pdf"
                maxSizeInMB={20}
                formats={['jpg', 'jpeg', 'png', 'pdf']}
                onFilesAdded={(files) => { registrationFileRef.current = files }}
                onDelete={() => { registrationFileRef.current = [] }}
                onValidationError={onValidationError}
                error={fieldErrors.registration_file?.[0]}
                onClearError={() => clearFieldError('registration_file')}
              />
            </div>
          </div>

          {/* International passport */}
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">{t('documents.international_passport_title')}</h5>
              <DocumentTextFormGroup
                id="international_passport_name"
                label={t('documents.international_passport_name')}
                placeholder={t('documents.international_passport_placeholder')}
                value={internationalPassportName}
                onChange={setInternationalPassportName}
                isEmptyInDrive={isInternationalTextEmpty}
                notFilledLabel={notFilledLabel}
                error={fieldErrors.international_passport_name?.[0]}
                onClearError={() => clearFieldError('international_passport_name')}
              />
              <DocumentFileFormGroup
                label={t('documents.international_passport_file')}
                isEmptyInDrive={isInternationalFilesEmpty}
                notFilledLabel={notFilledLabel}
                uploaderKey={internationalKey}
                multiple={false}
                accept=".jpg,.jpeg,.png,.pdf"
                maxSizeInMB={20}
                formats={['jpg', 'jpeg', 'png', 'pdf']}
                onFilesAdded={(files) => { internationalPassportFileRef.current = files }}
                onDelete={() => { internationalPassportFileRef.current = [] }}
                onValidationError={onValidationError}
                error={fieldErrors.international_passport_file?.[0]}
                onClearError={() => clearFieldError('international_passport_file')}
              />
            </div>
          </div>

          {/* State register extract */}
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">{t('documents.state_register_title')}</h5>
              <DocumentFileFormGroup
                label={t('documents.state_register_file')}
                isEmptyInDrive={isStateRegisterFilesEmpty}
                notFilledLabel={notFilledLabel}
                uploaderKey={stateRegisterKey}
                multiple={false}
                accept=".pdf"
                maxSizeInMB={20}
                formats={['pdf']}
                onFilesAdded={(files) => { stateRegisterFileRef.current = files }}
                onDelete={() => { stateRegisterFileRef.current = [] }}
                onValidationError={onValidationError}
                error={fieldErrors.state_register_extract_file?.[0]}
                onClearError={() => clearFieldError('state_register_extract_file')}
                hint={t('documents.state_register_hint')}
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <button
          type="button"
          className="btn waves-effect waves-light btn-info mt-2"
          onClick={handleSubmit}
        >
          {t('documents.save_button')}
        </button>
      </div>
    </div>
  )
}
